/**
 * Domain types and Zod schemas for the DraftEdge Laravel API.
 *
 * Phase 2 endpoints (auth, account, payments, tokens) are wired and the shapes
 * below mirror the live JSON returned by the controllers (verified 2026-04-25).
 *
 * Phase 3+ endpoints (players, sport dashboards, projections) are still
 * placeholders; once Laravel is wired up with `scribe` or `l5-swagger`, this
 * file should be regenerated from `laravel/storage/api-docs/openapi.json`
 * using `openapi-typescript-codegen` (or similar). See `openapi.placeholder.json`.
 */
import { z } from 'zod';

// ---------- Auth & user --------------------------------------------------
//
// Shapes mirror the live Laravel `/v2/auth/*` endpoints (verified 2026-04-25).
// Most fields are nullable because the legacy DB allows it; the SPA copes by
// defaulting to empty strings in the UI layer.

export const UserSchema = z.object({
  id: z.number().int().positive(),
  username: z.string().nullable().optional(),
  email: z.string().email(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  // Legacy `name` column may still exist on some user rows.
  name: z.string().nullable().optional(),
  avatar_url: z.string().url().nullable().optional(),
  created_at: z.string().nullable().optional(), // ISO 8601 or "Y-m-d H:i:s"
  email_verified_at: z.string().nullable().optional(),
  has_subscription: z.boolean().optional(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Login accepts either email or username in a single `login` field — matches
 * the legacy auth flow that the v2 controller preserves.
 */
export const LoginRequestSchema = z.object({
  login: z.string().min(1),
  password: z.string().min(1),
  /** Server expects `1` to issue a longer-lived token. */
  token: z.literal(1).optional(),
  remember: z.boolean().optional(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const RegisterRequestSchema = z.object({
  username: z.string().min(3),
  email: z.string().email(),
  password: z.string().min(8),
  confirm_password: z.string().min(8),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  agree_terms: z.boolean(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const AuthResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  user: UserSchema,
  token: z.string(),
  token_type: z.string().optional(),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

export const RegisterResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  user_id: z.number().int().positive().optional(),
});
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

export const SimpleSuccessSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});
export type SimpleSuccess = z.infer<typeof SimpleSuccessSchema>;

/** Response shape of GET /v2/auth/me. */
export const MeResponseSchema = z.object({
  success: z.boolean(),
  user: UserSchema,
  tokens: z
    .object({
      balance: z.number().int().nonnegative(),
      lifetime: z.number().int().nonnegative(),
      spent: z.number().int().nonnegative(),
    })
    .optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ---------- Profile (extended user, /v2/account/profile) -----------------

/**
 * Extra profile fields persisted alongside the core User row. Server may
 * return any of these as null while the user has not filled them in.
 */
export const ProfileSchema = z.object({
  bio: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  avatar_url: z.string().url().nullable().optional(),
  timezone: z.string().nullable().optional(),
  // Forward-compatible: server may add fields without breaking the SPA.
}).passthrough();
export type Profile = z.infer<typeof ProfileSchema>;

export const UpdateProfileRequestSchema = z.object({
  first_name: z.string().min(1).optional(),
  last_name: z.string().min(1).optional(),
  phone: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  avatar_url: z.string().url().nullable().optional(),
  timezone: z.string().nullable().optional(),
});
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

// ---------- Subscription / payments --------------------------------------

/**
 * Subscription as returned by /v2/account/profile and /v2/account/cancel-subscription.
 * The legacy DB stores statuses as strings, so we accept anything but recognize
 * the canonical Stripe-ish set.
 */
export const SubscriptionSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  status: z.string().optional(),
  plan: z.string().nullable().optional(),
  plan_slug: z.string().nullable().optional(),
  plan_name: z.string().nullable().optional(),
  current_period_end: z.string().nullable().optional(),
  cancel_at_period_end: z.boolean().optional(),
  cancelled_at: z.string().nullable().optional(),
  is_premium: z.boolean().optional(),
}).passthrough();
export type Subscription = z.infer<typeof SubscriptionSchema>;

/** Profile bag returned by GET /v2/account/profile. */
export const ProfileResponseSchema = z.object({
  success: z.boolean(),
  user: UserSchema,
  profile: ProfileSchema.nullable(),
  subscription: SubscriptionSchema.nullable(),
  tokens: z.object({
    balance: z.number().int().nonnegative(),
    lifetime: z.number().int().nonnegative(),
    spent: z.number().int().nonnegative(),
    monthly_used: z.number().int().nonnegative(),
    monthly_limit: z.number().int(),
    monthly_remaining: z.number().int(),
  }),
});
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

export const CancelSubscriptionResponseSchema = z.object({
  success: z.boolean(),
  subscription: SubscriptionSchema,
  message: z.string().optional(),
});
export type CancelSubscriptionResponse = z.infer<
  typeof CancelSubscriptionResponseSchema
>;

// ---------- Tokens (in-app currency) -------------------------------------

/**
 * Rich balance response from GET /v2/tokens/balance — includes both lifetime
 * counters AND monthly entitlement metadata so the SPA can render usage bars.
 */
export const TokenBalanceSchema = z.object({
  success: z.boolean().optional(),
  balance: z.number().int().nonnegative(),
  lifetime: z.number().int().nonnegative(),
  spent: z.number().int().nonnegative(),
  monthly_used: z.number().int().nonnegative(),
  monthly_limit: z.number().int(),
  monthly_remaining: z.number().int(),
  daily_remaining: z.number().int().optional(),
  subscription_plan: z.string().nullable().optional(),
  is_premium: z.boolean().optional(),
  /** Server may report a fractional 0–100 percentage. */
  usage_percentage: z.number().optional(),
});
export type TokenBalance = z.infer<typeof TokenBalanceSchema>;

/** A short, well-known set of features the server allows token deductions for. */
export const TokenFeatureSchema = z.enum([
  'draft_optimizer',
  'projection_view',
  'rankings_view',
  'news_summary',
  'lineup_optimizer',
  'trade_analyzer',
  'waiver_wire',
  'matchup_analysis',
]);
export type TokenFeature = z.infer<typeof TokenFeatureSchema>;

export const TokenDeductRequestSchema = z.object({
  amount: z.number().int().min(1).max(1000),
  feature: TokenFeatureSchema,
  reference_id: z.string().optional(),
});
export type TokenDeductRequest = z.infer<typeof TokenDeductRequestSchema>;

export const TokenDeductResponseSchema = z.object({
  success: z.boolean(),
  deducted: z.number().int().nonnegative(),
  new_balance: z.number().int().nonnegative(),
  transaction_id: z.union([z.string(), z.number()]).optional(),
});
export type TokenDeductResponse = z.infer<typeof TokenDeductResponseSchema>;

// ---------- Payments / checkout -----------------------------------------

export const CheckoutRequestSchema = z.object({
  plan_slug: z.string().min(1),
  success_url: z.string().url(),
  cancel_url: z.string().url(),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>;

export const PaypalCheckoutRequestSchema = z.object({
  plan_slug: z.string().min(1),
  return_url: z.string().url(),
  cancel_url: z.string().url(),
});
export type PaypalCheckoutRequest = z.infer<typeof PaypalCheckoutRequestSchema>;

export const CheckoutResponseSchema = z.object({
  success: z.boolean(),
  checkout_url: z.string().url(),
  session_id: z.string().optional(),
});
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;

export const PaypalCheckoutResponseSchema = z.object({
  success: z.boolean(),
  approve_url: z.string().url(),
});
export type PaypalCheckoutResponse = z.infer<
  typeof PaypalCheckoutResponseSchema
>;

// ---------- Players & rankings -------------------------------------------

export const SportSchema = z.enum(['nfl', 'mlb', 'nba', 'nhl', 'cfb', 'cbb']);
export type Sport = z.infer<typeof SportSchema>;

export const PlayerSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  position: z.string(),
  team: z.string(),
  sport: SportSchema,
  rank: z.number().int().nullable(),
  adp: z.number().nullable(),
  projection: z.number().nullable(),
  bye_week: z.number().int().nullable(),
  injury_status: z.string().nullable(),
  photo_url: z.string().url().nullable(),
});
export type Player = z.infer<typeof PlayerSchema>;

export const PlayerListQuerySchema = z.object({
  q: z.string().optional(),
  position: z.string().optional(),
  team: z.string().optional(),
  sport: SportSchema.optional(),
  limit: z.number().int().min(1).max(500).optional(),
  offset: z.number().int().nonnegative().optional(),
});
export type PlayerListQuery = z.infer<typeof PlayerListQuerySchema>;

export const PlayerListResponseSchema = z.object({
  data: z.array(PlayerSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type PlayerListResponse = z.infer<typeof PlayerListResponseSchema>;

// ---------- NFL cheatsheet players (live /v2/nfl/players, file.json source) -

/**
 * Player row served by /v2/nfl/players. Source data is `file.json` (526 ranked
 * players) for premium tier or `limited.json` (10 players) for free/anon tier.
 *
 * Field names mirror the source JSON so the controller can pass-through with
 * minimal mapping. `passthrough()` keeps unknown keys (Seasons, contract,
 * narrative metadata) intact for the future detail page.
 */
export const NflPlayerSchema = z
  .object({
    PlayerID: z.union([z.number(), z.string()]).optional(),
    Player_ID: z.union([z.number(), z.string()]).optional(),
    Player: z.string().optional(),
    PlayerName: z.string().optional(),
    Team: z.string().optional(),
    Pos: z.string().optional(),
    PosRank: z.string().nullable().optional(),
    ADP: z.union([z.number(), z.string()]).nullable().optional(),
    Rk: z.union([z.number(), z.string()]).nullable().optional(),
    BYEWEEK: z.union([z.number(), z.string()]).nullable().optional(),
    Bye: z.union([z.number(), z.string()]).nullable().optional(),
    Status: z.string().nullable().optional(),
    // Projections — server may emit any subset; UI sums or picks what it needs.
    Proj_FP: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_Rec: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_RecYds: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_RecTD: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_RushYds: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_RushTD: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_PassYd: z.union([z.number(), z.string()]).nullable().optional(),
    Proj_PassTD: z.union([z.number(), z.string()]).nullable().optional(),
  })
  .passthrough();
export type NflPlayer = z.infer<typeof NflPlayerSchema>;

/**
 * Which bi-weekly rankings drop the served pool came from. Every field is
 * nullable — an older patch file may not carry all three.
 */
export const RankingsDropSchema = z
  .object({
    id: z.string().nullish(),
    date: z.string().nullish(),
    generatedAt: z.string().nullish(),
  })
  .passthrough();
export type RankingsDrop = z.infer<typeof RankingsDropSchema>;

/**
 * GET /v2/nfl/players envelope — same `Paginated<T>` shape as news plus a
 * `tier` discriminator so the SPA can show a "Premium" / "Free" badge on
 * the cheatsheet card without a second profile lookup.
 */
export const CheatsheetResponseSchema = z
  .object({
    data: z.array(NflPlayerSchema),
    page: z.number().int().nonnegative(),
    per_page: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    total_pages: z.number().int().nonnegative(),
    tier: z.enum(['premium', 'free']).optional(),
    // Identity of the bi-weekly expert-rankings drop these rows came from.
    // A saved cheat sheet is a frozen ORDER, so it does not move when a drop
    // lands — stamping this at seed time is how the app knows a board went
    // stale. Nullable: no patch file on disk means no drop.
    drop: RankingsDropSchema.nullish(),
  })
  .passthrough();
export type CheatsheetResponse = Paginated<NflPlayer> & {
  tier?: 'premium' | 'free';
  drop?: RankingsDrop | null;
};

/**
 * Trending (waiver-wire mover) row served by /v2/nfl/trending. Source data is
 * `trending1.json` aggregated weekly. `delta_pct` is positive for risers and
 * negative for fallers; the controller computes it from add/drop counts.
 */
export const TrendingPlayerSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    team: z.string().optional(),
    position: z.string().optional(),
    delta_pct: z.number().optional(),
    add_count: z.number().int().optional(),
    drop_count: z.number().int().optional(),
  })
  .passthrough();
export type TrendingPlayer = z.infer<typeof TrendingPlayerSchema>;

/** POST /v2/nfl/players/resolve → bulk Sleeper-ID identity lookup. */
export const ResolvedPlayerSchema = z
  .object({
    name: z.string(),
    position: z.string().nullable().optional(),
    team: z.string().nullable().optional(),
    injury_status: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
  })
  .passthrough();
export type ResolvedPlayer = z.infer<typeof ResolvedPlayerSchema>;

export const ResolvePlayersResponseSchema = z.object({
  players: z.record(ResolvedPlayerSchema),
});
export type ResolvePlayersResponse = z.infer<typeof ResolvePlayersResponseSchema>;

/** GET /v2/nfl/depth-charts → team depth charts + league injury list. */
export const DepthPlayerSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    order: z.number().int(),
    injury_status: z.string().nullable().optional(),
    age: z.number().nullable().optional(),
    years_exp: z.number().nullable().optional(),
    number: z.number().nullable().optional(),
  })
  .passthrough();
export type DepthPlayer = z.infer<typeof DepthPlayerSchema>;

export const InjuredPlayerSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    team: z.string(),
    position: z.string(),
    injury_status: z.string(),
    injury_body_part: z.string().nullable().optional(),
    injury_notes: z.string().nullable().optional(),
    depth_order: z.number().int().nullable().optional(),
  })
  .passthrough();
export type InjuredPlayer = z.infer<typeof InjuredPlayerSchema>;

export const DepthChartsResponseSchema = z.object({
  last_updated: z.string().nullable().optional(),
  teams: z.record(z.record(z.array(DepthPlayerSchema))),
  injuries: z.array(InjuredPlayerSchema),
});
export type DepthChartsResponse = z.infer<typeof DepthChartsResponseSchema>;

/** GET /v2/nfl/trending → top movers + weekly leaders + projection count. */
export const TrendingResponseSchema = z
  .object({
    week: z.number().int().nonnegative().optional(),
    leaders: z.array(z.unknown()).optional(),
    gainers: z.array(TrendingPlayerSchema).optional(),
    fallers: z.array(TrendingPlayerSchema).optional(),
    projection_count: z.number().int().nonnegative().optional(),
    /** ISO stamp of the builder run — drives "Updated Xh ago" on /trending. */
    last_updated: z.string().nullable().optional(),
  })
  .passthrough();
export type TrendingResponse = z.infer<typeof TrendingResponseSchema>;

/**
 * Single narrative scouting report served by /v2/nfl/player-previews. Source
 * data is `player_previews.json`. The `preview` body is a multi-paragraph
 * markdown-ish string; `links` references source articles cited by the AI.
 *
 * Tolerant of both shapes:
 *   - Legacy list rows return `{playerID, preview, links?, timestamp?}` where
 *     `links` is an array of {url,title,description?,date?} objects (the AI's
 *     source citations).
 *   - The single-player accessor /v2/nfl/player-previews/{playerId} returns
 *     the same row plus an optional resolved `name` for display convenience.
 *
 * Both `playerID` (legacy snake-ish camel) and `playerId` (the new SPA-side
 * naming) are accepted to keep the SPA forward-compatible while the backend
 * settles on a final shape.
 */
export const PlayerPreviewLinkSchema = z
  .union([
    z.string(),
    z
      .object({
        url: z.string().optional(),
        title: z.string().optional(),
        description: z.string().optional(),
        date: z.string().optional(),
      })
      .passthrough(),
  ]);
export type PlayerPreviewLink = z.infer<typeof PlayerPreviewLinkSchema>;

export const PlayerPreviewSchema = z
  .object({
    playerID: z.union([z.number(), z.string()]).optional(),
    playerId: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    preview: z.string(),
    links: z.array(PlayerPreviewLinkSchema).optional(),
    timestamp: z.union([z.number(), z.string()]).nullable().optional(),
  })
  .passthrough();
export type PlayerPreview = z.infer<typeof PlayerPreviewSchema>;

/** GET /v2/nfl/player-previews → list envelope with optional limit. */
export const PlayerPreviewListResponseSchema = z
  .object({
    data: z.array(PlayerPreviewSchema),
    total: z.number().int().nonnegative(),
  })
  .passthrough();
export type PlayerPreviewListResponse = z.infer<
  typeof PlayerPreviewListResponseSchema
>;

/**
 * Per-game entry from /v2/nfl/team-schedule/{team}. `week` is the ordinal
 * (1..N) after sorting by date, NOT the official NFL week number — close
 * enough for UI ordering, and stable within a single team. `defRankVsPos`
 * is null when the opponent isn't represented in defense-vs-position.json.
 */
export const TeamScheduleGameSchema = z.object({
  week: z.number().int().positive(),
  date: z.string(),
  opponent: z.string(),
  opponent_full: z.string().optional(),
  isHome: z.boolean(),
  defRankVsPos: z.object({
    QB: z.number().int().nullable(),
    RB: z.number().int().nullable(),
    WR: z.number().int().nullable(),
    TE: z.number().int().nullable(),
  }),
});
export type TeamScheduleGame = z.infer<typeof TeamScheduleGameSchema>;

/** GET /v2/nfl/team-schedule/{team} → {team, games[]}. */
export const TeamScheduleResponseSchema = z.object({
  team: z.string(),
  games: z.array(TeamScheduleGameSchema),
});
export type TeamScheduleResponse = z.infer<typeof TeamScheduleResponseSchema>;

/**
 * One game row from GET /v2/nfl/players/{id}/gamelog. Stat fields are
 * optional PascalCase numbers matching the `Seasons` dict convention
 * (Targets, Receptions, RecYards, PassYards, ...) — the pipeline omits
 * zero-valued stats, so passthrough + treat-missing-as-0 in the UI.
 */
export const PlayerGamelogGameSchema = z
  .object({
    season: z.string(),
    date: z.string(),
    opp: z.string().optional(),
    home: z.number().optional(),
  })
  .passthrough();
export type PlayerGamelogGame = z.infer<typeof PlayerGamelogGameSchema>;

/** GET /v2/nfl/players/{id}/gamelog → per-player game-by-game history. */
export const PlayerGamelogResponseSchema = z
  .object({
    playerId: z.string(),
    seasons: z.array(z.string()),
    games: z.array(PlayerGamelogGameSchema),
  })
  .passthrough();
export type PlayerGamelogResponse = z.infer<
  typeof PlayerGamelogResponseSchema
>;

/** One 2026-class rookie from GET /v2/nfl/rookies. collegeStats carries the
 * final college season (PassYards/RushYards/Receptions/... ints) or null
 * when no FBS stats are on file — never estimated. */
export const RookieSchema = z
  .object({
    sleeperId: z.string(),
    name: z.string(),
    position: z.string(),
    team: z.string().nullable().optional(),
    college: z.string().nullable().optional(),
    age: z.number().nullable().optional(),
    height: z.union([z.string(), z.number()]).nullable().optional(),
    weight: z.union([z.string(), z.number()]).nullable().optional(),
    jersey: z.union([z.string(), z.number()]).nullable().optional(),
    searchRank: z.number().nullable().optional(),
    injuryStatus: z.string().nullable().optional(),
    collegeStats: z.record(z.number()).nullable().optional(),
    /** Expert-board data for the ranked subset; null for the rest. */
    expert: z
      .object({
        rookieRank: z.number().nullable().optional(),
        boardRank: z.number().nullable().optional(),
        posRank: z.string().nullable().optional(),
        tier: z.number().nullable().optional(),
        bye: z.number().nullable().optional(),
        rangeMin: z.number().nullable().optional(),
        rangeMax: z.number().nullable().optional(),
        projPprPts: z.number().nullable().optional(),
        trendDelta: z.number().nullable().optional(),
        dropDate: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();
export type Rookie = z.infer<typeof RookieSchema>;

/** GET /v2/nfl/rookies → the whole class, pre-ordered by the backend. */
export const RookiesResponseSchema = z
  .object({
    class: z.string().nullable().optional(),
    collegeSeason: z.string().nullable().optional(),
    orderedBy: z.string().nullable().optional(),
    players: z.array(RookieSchema),
  })
  .passthrough();
export type RookiesResponse = z.infer<typeof RookiesResponseSchema>;

// ---------- Sport dashboards & projections -------------------------------

/**
 * Sport-code alias kept in sync with `SportSchema` above. Exported separately
 * so SPA code reads `SportCode` (a name that matches the route param) without
 * pulling in the broader `Sport` type from the player module.
 */
export const SportCodeSchema = SportSchema;
export type SportCode = z.infer<typeof SportCodeSchema>;

/**
 * Generic Laravel paginator envelope. The new `/v2/*` controllers all return
 * `{data, page, per_page, total, total_pages}` — keep one schema so callers
 * never invent ad-hoc pagination shapes.
 */
export const PaginatedSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    data: z.array(item),
    page: z.number().int().nonnegative(),
    per_page: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    total_pages: z.number().int().nonnegative(),
  });
export type Paginated<T> = {
  data: T[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

/**
 * Legacy `SportDashboardSchema` kept for backward compatibility with the
 * stub endpoint shape. The new `/v2/{sport}/dashboard` endpoint returns a
 * loose JSON object whose shape varies per sport; consume that with
 * `SportDashboardResponseSchema` instead.
 */
export const SportDashboardSchema = z.object({
  sport: SportSchema,
  top_players: z.array(PlayerSchema),
  trending: z.array(PlayerSchema),
  news: z.array(
    z.object({
      id: z.number().int(),
      title: z.string(),
      url: z.string().url(),
      published_at: z.string(),
    }),
  ),
});
export type SportDashboard = z.infer<typeof SportDashboardSchema>;

/**
 * Loose schema for the live /v2/{sport}/dashboard response. JSON shapes vary
 * by sport (NFL returns one structure, NBA another) — accept anything and let
 * the UI render defensively. `passthrough` preserves unknown keys so tabs/
 * widgets can pick what they need without losing data.
 */
export const SportDashboardResponseSchema = z
  .record(z.unknown())
  .or(z.array(z.unknown()));
export type SportDashboardResponse =
  | Record<string, unknown>
  | unknown[];

export const ProjectionRowSchema = z.object({
  player_id: z.number().int(),
  player_name: z.string(),
  position: z.string(),
  team: z.string(),
  projection: z.number(),
  rank: z.number().int(),
  tier: z.number().int().nullable(),
});
export type ProjectionRow = z.infer<typeof ProjectionRowSchema>;

/** Legacy stub shape; preserved for older callers. */
export const ProjectionsResponseSchema = z.object({
  sport: SportSchema,
  generated_at: z.string(),
  rows: z.array(ProjectionRowSchema),
});
export type ProjectionsResponse = z.infer<typeof ProjectionsResponseSchema>;

/**
 * Live `/v2/{sport}/projections` row — fields are best-effort because Laravel
 * returns whatever the per-sport service produces. UI must treat fields as
 * optional and fall back gracefully.
 */
export const SportProjectionRowSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    player_id: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    player_name: z.string().optional(),
    position: z.string().optional(),
    team: z.string().optional(),
    opponent: z.string().nullable().optional(),
    projection: z.number().nullable().optional(),
    points: z.number().nullable().optional(),
    rank: z.number().int().nullable().optional(),
    tier: z.number().int().nullable().optional(),
  })
  .passthrough();
export type SportProjectionRow = z.infer<typeof SportProjectionRowSchema>;

export const SportProjectionsResponseSchema = PaginatedSchema(
  SportProjectionRowSchema,
);
export type SportProjectionsResponse = Paginated<SportProjectionRow>;

/**
 * Schedule / injuries / power rankings shapes are still being finalized on
 * the Laravel side. Accept loose JSON (object OR array OR paginated) so the
 * UI keeps working as the backend stabilizes — `passthrough` preserves
 * unknown fields verbatim.
 */
export const SportScheduleResponseSchema = z
  .record(z.unknown())
  .or(z.array(z.unknown()));
export type SportScheduleResponse =
  | Record<string, unknown>
  | unknown[];

export const SportInjuriesResponseSchema = z
  .record(z.unknown())
  .or(z.array(z.unknown()));
export type SportInjuriesResponse =
  | Record<string, unknown>
  | unknown[];

export const SportPowerRankingsResponseSchema = z
  .record(z.unknown())
  .or(z.array(z.unknown()));
export type SportPowerRankingsResponse =
  | Record<string, unknown>
  | unknown[];

// ---------- Drafts (NFL fantasy football) -------------------------------
//
// Mirrors the legacy `DraftLeagues` table preserved by the
// `2026_04_26_000004_create_draft_leagues_table.php` migration. Field names
// match the legacy mixedCase column names because the Laravel /v2/drafts
// controllers are serializing them straight through (see laravel-bootstrap/
// drafts/Http/* — which is being implemented in parallel; if a payload ships
// with snake_case aliases later, add them here as optional fallbacks).
//
// `draftData` carries a JSON blob of the full leagueMgmt state (players
// drafted, team-by-team rosters, pick history) — we keep it as a passthrough
// `unknown` because the SPA does not need to inspect its internals at the
// list level. The /drafts/:id detail screen will narrow it later.

/**
 * Compact summary used by GET /v2/drafts. Matches the SELECT in legacy
 * get_leagues.php: id, leagueName, myTeam, rounds, teams, currentPick, created_at.
 */
export const DraftSummarySchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    leagueName: z.string(),
    myTeam: z.string().nullable().optional(),
    teams: z.union([z.string(), z.number()]).nullable().optional(),
    rounds: z.union([z.string(), z.number()]).nullable().optional(),
    currentPick: z.union([z.string(), z.number()]).nullable().optional(),
    created_at: z.string().nullable().optional(),
    /**
     * True when this draft is a MOCK (CPU drafts every team but yours). The
     * flag itself lives in `draftData.mockMode`, which the list endpoint does
     * not ship; the server lifts it into the summary so the SPA can split the
     * Drafts and Mock Drafts tabs without fetching each draft in full.
     *
     * Optional on purpose: an app build newer than the deployed API sees it
     * missing, and `undefined` must read as "not a mock" so real drafts keep
     * showing up on the Drafts tab rather than vanishing.
     */
    mockMode: z.boolean().nullable().optional(),
    /**
     * True when this room mirrors a real Sleeper draft (draftData.sleeperSync
     * has a draftId). Lifted into the summary by the server for the same
     * reason as `mockMode` — the list does not ship draftData.
     *
     * Optional: older APIs omit it, and the SPA falls back to its local
     * league->room map, which only knows about drafts created on THIS device.
     */
    sleeper: z.boolean().nullable().optional(),
    /**
     * The Sleeper LEAGUE id this room mirrors, when there is one. Lets the
     * Drafts list join against the same live "glance" (phase, picks until
     * your turn, scheduled start) that the Home screen renders, instead of
     * inverting a device-local league->room map.
     */
    sleeperLeagueId: z.string().nullable().optional(),
  })
  .passthrough();
export type DraftSummary = z.infer<typeof DraftSummarySchema>;

/**
 * Full draft as returned by GET /v2/drafts/{id}. Adds `draftData` (JSON blob
 * of the live draft state) and `aiAdvice` (history of AI Draft Coach calls).
 */
export const DraftSchema = DraftSummarySchema.extend({
  draftData: z.unknown().nullable().optional(),
  aiAdvice: z.unknown().nullable().optional(),
  userEmail: z.string().nullable().optional(),
});
export type Draft = z.infer<typeof DraftSchema>;

// ---------- League configuration (Phase 0.1) ----------------------------
//
// Persisted at `draftData.leagueConfig`. Both the SPA (rosterFill, Need
// Radar, create wizard) and the Laravel AI prompt builder read this exact
// shape, so it's the single source of truth for scoring + roster shape +
// league size. Tolerant by design: every field has a default, parsing a
// partial or empty object yields a complete config (see resolveLeagueConfig
// in packages/web/src/lib/leagueConfig.ts, which owns final defaulting).

/**
 * Scoring profile. Phase 0.1 CAPTURES these so the AI Coach prompt reflects
 * the league's scoring; RECOMPUTING projected points from these weights is
 * Phase 0.2 — until then the pool still sorts on the server's precomputed
 * Proj_FP.
 */
export const LeagueScoringSchema = z.object({
  /** Points per reception: 0 = standard, 0.5 = half-PPR, 1 = full-PPR. */
  reception: z.number().min(0).max(2).default(1),
  /** Points per passing TD: 4 (default) or 6. */
  passTd: z.number().min(0).max(6).default(4),
  /** Bonus points per TE reception on top of `reception` (TE-premium). */
  tePremiumBonus: z.number().min(0).max(2).default(0),
});
export type LeagueScoring = z.infer<typeof LeagueScoringSchema>;

/**
 * Roster construction. Every count is a starter-slot count except `bench`.
 * `flex` accepts RB/WR/TE; `superflex` additionally accepts QB (enables
 * 2-QB / Superflex leagues). Keys are lowercase to match rosterFill's
 * RosterFillConfig 1:1 so mapping is an identity.
 */
export const LeagueRosterSchema = z.object({
  qb: z.number().int().min(0).max(4).default(1),
  rb: z.number().int().min(0).max(6).default(2),
  wr: z.number().int().min(0).max(6).default(3),
  te: z.number().int().min(0).max(4).default(1),
  flex: z.number().int().min(0).max(4).default(1),
  superflex: z.number().int().min(0).max(2).default(0),
  k: z.number().int().min(0).max(2).default(1),
  def: z.number().int().min(0).max(2).default(1),
  bench: z.number().int().min(0).max(15).default(6),
});
export type LeagueRoster = z.infer<typeof LeagueRosterSchema>;

/** Preset identifiers the create wizard offers. 'custom' = user-edited. */
export const LEAGUE_PRESETS = [
  'standard',
  'half_ppr',
  'full_ppr',
  'superflex',
  'best_ball',
  'custom',
] as const;
export const LeaguePresetSchema = z.enum(LEAGUE_PRESETS);
export type LeaguePreset = z.infer<typeof LeaguePresetSchema>;

/**
 * Full league configuration. Sub-objects are optional at the schema layer
 * (legacy / partial blobs parse cleanly); the SPA's resolveLeagueConfig
 * fills any gaps with preset/standard defaults before use.
 */
/** Draft format (Phase 5.1). Absent = 'snake' (every pre-5.1 draft). */
export const DraftFormatSchema = z.enum(['snake', 'auction']);
export type DraftFormat = z.infer<typeof DraftFormatSchema>;

export const LeagueConfigSchema = z
  .object({
    preset: LeaguePresetSchema.optional(),
    roster: LeagueRosterSchema.optional(),
    scoring: LeagueScoringSchema.optional(),
    /** 'snake' (default) or 'auction' ($ budget, nominations, prices). */
    format: DraftFormatSchema.optional(),
    /** Auction only: per-team budget in dollars. Default 200. */
    auctionBudget: z.number().int().min(10).max(10000).optional(),
    /**
     * Best Ball (Phase 5.3): no in-season management — weekly lineups are
     * auto-optimized. Orthogonal to `format` (best-ball drafts are snake).
     * Tunes advice (byes don't conflict, stacks matter more) + Coach prompt.
     */
    bestBall: z.boolean().optional(),
  })
  .passthrough();
export type LeagueConfig = z.infer<typeof LeagueConfigSchema>;

// ---- League import from third-party platforms (Yahoo now; ESPN later) ------

/** GET /v2/import/yahoo/status */
export interface YahooImportStatus {
  /** Server has Yahoo OAuth creds configured (owner registered the app). */
  configured: boolean;
  /** This user has connected their Yahoo account. */
  connected: boolean;
}

/** One row in the Yahoo league picker (GET /v2/import/yahoo/leagues). */
/** GET /v2/import/espn/status. `enabled` is the server kill-switch —
 *  unlike Yahoo there is no app registration, so no `configured` concept. */
export interface EspnImportStatus {
  enabled: boolean;
  connected: boolean;
}

export interface YahooLeagueSummary {
  leagueId: string; // Yahoo league_key, e.g. "449.l.12345"
  name: string;
  season: string;
  teams: number;
}

/**
 * Normalized result of importing a league from any platform. Mirrors the
 * server's LeagueImportResult::toArray() and the SPA's SleeperImportResult —
 * `roster`/`scoring` feed the draft wizard identically regardless of source.
 */
export interface PlatformLeagueImport {
  provider: string; // 'yahoo' | 'espn' | 'sleeper'
  leagueId: string;
  leagueName: string;
  season: string;
  teams: number;
  rounds: number;
  roster: LeagueRoster;
  scoring: LeagueScoring;
  ignoredPositions: string[];
  draft: { detectedSlot: number | null };
}

/** POST /v2/drafts body — wizard captures league setup + optional config. */
export const CreateDraftRequestSchema = z.object({
  leagueName: z.string().min(1).max(255),
  teams: z.number().int().min(2).max(30),
  rounds: z.number().int().min(1).max(30),
  myTeam: z.string().min(1).max(64),
  /**
   * Optional initial draftData. The wizard seeds `{ leagueConfig }` here so
   * the league's scoring + roster shape persist from pick one; Laravel's
   * StoreDraftRequest accepts `draftData` as a free-form array.
   */
  draftData: z
    .object({ leagueConfig: LeagueConfigSchema })
    .passthrough()
    .optional(),
});
export type CreateDraftRequest = z.infer<typeof CreateDraftRequestSchema>;

/**
 * Single recorded pick inside the `draftData.picks[]` blob.
 *
 * The SPA owns this schema — Laravel persists `draftData` as opaque JSON, so
 * we can evolve the shape freely without a backend migration. Fields chosen
 * to be enough to render a draft board and look up rosters without re-hitting
 * /v2/nfl/players for every pick.
 */
export const DraftPickSchema = z.object({
  /** 1-indexed overall pick number (1 .. rounds*teams). */
  pickNumber: z.number().int().positive(),
  /** 1-indexed round number. */
  round: z.number().int().positive(),
  /** 1-indexed team slot that owns this pick (snake-corrected). */
  slot: z.number().int().positive(),
  /** True if `slot === Number(draft.myTeam)` at the time of the pick. */
  isUser: z.boolean(),
  /** Player id from /v2/nfl/players — could be number or string. */
  playerId: z.union([z.number(), z.string()]),
  playerName: z.string(),
  position: z.string(),
  team: z.string(),
  /** Player's ADP at pick time — lets the board flag reaches vs. values. */
  adp: z.number().nullable().optional(),
  /**
   * Auction only (Phase 5.1): winning bid in dollars. Absent on snake picks.
   * In auction drafts `pickNumber` is the acquisition (lot) order and `slot`
   * is the winning team — there is no snake order.
   */
  price: z.number().optional(),
  /** ISO 8601 timestamp the pick was recorded client-side. */
  pickedAt: z.string(),
});
export type DraftPick = z.infer<typeof DraftPickSchema>;

/**
 * A keeper / holdover — a player a team brings into the draft from a prior
 * season. Keeper leagues vary wildly, so we model the two common cases:
 *
 *   - `round === null` → "floating" keeper: the player is simply pre-rostered
 *     to `slot` and removed from the pool. No draft pick is consumed.
 *   - `round` set      → "round-pinned" keeper (Phase 2): the player occupies
 *     that team's snake pick in `round`, and that slot is skipped when the
 *     draft reaches it. Stored here now so the shape is forward-compatible.
 */
export const KeeperSchema = z.object({
  /** 1-indexed team slot that owns this keeper. */
  slot: z.number().int().positive(),
  /** Player id from /v2/nfl/players — could be number or string. */
  playerId: z.union([z.number(), z.string()]),
  playerName: z.string(),
  position: z.string(),
  team: z.string(),
  /** Round the keeper costs, or null for a floating (no-cost) keeper. */
  round: z.number().int().positive().nullable().default(null),
});
export type Keeper = z.infer<typeof KeeperSchema>;

/**
 * Full `draftData` JSON blob shape. Tolerant: `picks` may be missing on legacy
 * rows (free-form `draftData` from the legacy PHP draft tool), so callers
 * `safeParse` and fall back to `{picks: []}` on shape mismatch. `keepers`
 * defaults to `[]` for any draft created before keeper support landed.
 */
export const DraftDataBlobSchema = z
  .object({
    picks: z.array(DraftPickSchema).default([]),
    keepers: z.array(KeeperSchema).default([]),
    /**
     * League configuration (scoring + roster shape). Optional: drafts created
     * before Phase 0.1 won't have it, so consumers resolve defaults via
     * resolveLeagueConfig. New drafts seed it at creation from the wizard.
     */
    leagueConfig: LeagueConfigSchema.optional(),
    /**
     * Mock Draft Mode (Phase 1): when true, every team except the user's slot
     * is auto-drafted by the CPU opponent engine. Absent/false = the legacy
     * "I pick for everyone" manual mode.
     */
    mockMode: z.boolean().optional(),
    /**
     * Draft queue / watchlist (Phase 2.1): player ids (string form) the user
     * has starred as targets, in priority order. Drafted players are filtered
     * out of the displayed queue.
     */
    queue: z.array(z.string()).optional(),
    /**
     * Live Sleeper draft sync (Phase 6.1): when set, the room polls the
     * Sleeper draft and applies picks automatically. Cleared on disconnect.
     */
    sleeperSync: z
      .object({
        draftId: z.string(),
        leagueId: z.string().optional(),
        /** Scheduled start (unix ms) — drives the pre-draft countdown. */
        startTime: z.number().nullable().optional(),
      })
      .optional(),
  })
  .passthrough();
export type DraftDataBlob = z.infer<typeof DraftDataBlobSchema>;

/** PUT /v2/drafts/{id} body — partial update used by the live draft tool. */
export const UpdateDraftRequestSchema = z
  .object({
    leagueName: z.string().min(1).max(255).optional(),
    teams: z.union([z.string(), z.number()]).optional(),
    myTeam: z.string().nullable().optional(),
    rounds: z.union([z.string(), z.number()]).optional(),
    currentPick: z.union([z.string(), z.number()]).optional(),
    draftData: z.unknown().optional(),
  })
  .passthrough();
export type UpdateDraftRequest = z.infer<typeof UpdateDraftRequestSchema>;

/** POST /v2/drafts/{id}/advice body — append a coach interaction. */
export const AppendAdviceRequestSchema = z.object({
  prompt: z.string().min(1),
  response: z.string().optional(),
  meta: z.record(z.unknown()).optional(),
});
export type AppendAdviceRequest = z.infer<typeof AppendAdviceRequestSchema>;

/** GET /v2/drafts → { data: [...] } (paginated envelope, may degrade to bare array). */
export const DraftListResponseSchema = z
  .object({
    data: z.array(DraftSummarySchema),
    page: z.number().int().nonnegative().optional(),
    per_page: z.number().int().positive().optional(),
    total: z.number().int().nonnegative().optional(),
    total_pages: z.number().int().nonnegative().optional(),
  })
  .passthrough()
  .or(z.array(DraftSummarySchema));
export type DraftListResponse =
  | { data: DraftSummary[]; page?: number; per_page?: number; total?: number; total_pages?: number }
  | DraftSummary[];

/** GET/POST/PUT /v2/drafts/{id} → wraps the draft in `{success, data}` typically. */
export const DraftResponseSchema = z
  .object({
    success: z.boolean().optional(),
    data: DraftSchema.optional(),
    draft: DraftSchema.optional(),
  })
  .passthrough()
  .or(DraftSchema);
export type DraftResponse =
  | { success?: boolean; data?: Draft; draft?: Draft; [k: string]: unknown }
  | Draft;

export const DeleteDraftResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});
export type DeleteDraftResponse = z.infer<typeof DeleteDraftResponseSchema>;

// ---------- News ---------------------------------------------------------

export const NewsArticleSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    title: z.string(),
    content: z.string().nullable().optional(),
    sport: z.string().nullable().optional(),
    mytime: z.string().nullable().optional(),
    seotitle: z.string().nullable().optional(),
    author: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
  })
  .passthrough();
export type NewsArticle = z.infer<typeof NewsArticleSchema>;

export const NewsListResponseSchema = PaginatedSchema(NewsArticleSchema);
export type NewsListResponse = Paginated<NewsArticle>;

export const NewsArticleResponseSchema = z
  .object({
    data: NewsArticleSchema.optional(),
    article: NewsArticleSchema.optional(),
  })
  .passthrough()
  .or(NewsArticleSchema);
export type NewsArticleResponse = NewsArticle | {
  data?: NewsArticle;
  article?: NewsArticle;
  [k: string]: unknown;
};

// ---------- Membership plans (live /v2/membership-plans) ----------------

/**
 * Plan row served by /v2/membership-plans. Server returns BOTH canonical
 * fields (price_cents, billing_period, monthly_ai_tokens) AND SPA-friendly
 * aliases (plan_name, billing_cycle, price). All optional so we degrade
 * gracefully if a field is missing.
 */
export const MembershipPlanSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    slug: z.string(),
    name: z.string(),
    display_name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),

    // Canonical
    price_cents: z.number().int().nonnegative().nullable().optional(),
    billing_period: z.string().nullable().optional(),
    monthly_ai_tokens: z.number().int().nonnegative().nullable().optional(),
    daily_optimizer_builds: z
      .number()
      .int()
      .nonnegative()
      .nullable()
      .optional(),
    trial_days: z.number().int().nonnegative().nullable().optional(),
    stripe_price_id: z.string().nullable().optional(),
    paypal_plan_id: z.string().nullable().optional(),
    is_popular: z.boolean().nullable().optional(),
    sort_order: z.number().int().nullable().optional(),

    // SPA-friendly aliases — server emits both for convenience
    plan_name: z.string().nullable().optional(),
    billing_cycle: z.string().nullable().optional(),
    price: z.union([z.string(), z.number()]).nullable().optional(),

    /**
     * Set by the authenticated /v2/membership-plans endpoint to mark which
     * plan the current user is on. Lets SubscriptionCard render "Manage" vs
     * "Upgrade" without a separate profile lookup.
     */
    is_current: z.boolean().nullable().optional(),
  })
  .passthrough();
export type MembershipPlan = z.infer<typeof MembershipPlanSchema>;

export const MembershipPlansResponseSchema = z.object({
  plans: z.array(MembershipPlanSchema),
});
export type MembershipPlansResponse = z.infer<
  typeof MembershipPlansResponseSchema
>;

// ---------- Custom rankings / cheat-sheet (Phase 4.1) --------------------

/**
 * Where a sheet's initial order came from. Purely informational — the order
 * is materialised at seed time, so changing sources later never rewrites a
 * sheet the user has since edited.
 */
export const CheatSheetSeedSchema = z.enum(['adp', 'draftedge', 'projected']);
export type CheatSheetSeed = z.infer<typeof CheatSheetSeedSchema>;

/**
 * ONE cheat sheet. The editable fields are exactly what v1 stored flat at the
 * top of the blob — v1 documents migrate by becoming a single sheet.
 */
export const CheatSheetSchema = z
  .object({
    /** Stable client-generated id. */
    id: z.string(),
    /** User-facing name, e.g. "Zero RB" or "Best ball". */
    name: z.string(),
    /** Custom rank order, best first. Player ids in string form. */
    order: z.array(z.string()).default([]),
    /**
     * Player ids that START a new tier. Anchoring breaks to players (not
     * positional indexes) keeps tiers stable under reorder — the break
     * travels with the player it's attached to.
     */
    tierStarts: z.array(z.string()).default([]),
    /** Player ids flagged as draft targets (🎯). */
    targets: z.array(z.string()).default([]),
    /** Player ids flagged as do-not-draft (🚫). */
    avoids: z.array(z.string()).default([]),
    /** Free-text per-player notes, keyed by player id. */
    notes: z.record(z.string()).default({}),
    /** Which ranking this sheet was last seeded/reset from. */
    seededFrom: CheatSheetSeedSchema.default('adp'),
    /**
     * Which expert-rankings DROP the order was built against (dropId, or
     * dropDate when the patch carries no id). The order is frozen once
     * saved, so this is the only way to tell that a newer drop has landed
     * and the board is out of date. Absent on sheets created before this
     * existed — treat missing as "unknown", never as "stale".
     */
    seededFromDrop: z.string().nullish(),
    /** ISO 8601 client-side timestamp of the last edit. */
    updatedAt: z.string().optional(),
  })
  .passthrough();
export type CheatSheet = z.infer<typeof CheatSheetSchema>;

/**
 * The user's custom rankings blob — GET/PUT /v2/me/rankings. SPA-owned JSON,
 * opaque to Laravel (same contract as draftData): the server type-checks that
 * it is an object and caps it at 512KB, and otherwise never looks inside.
 *
 * That opacity is what lets MULTIPLE sheets ship without a schema change. The
 * `user_rankings` table still holds one row per user; the row now carries a
 * list of sheets instead of a single one.
 *
 * VERSIONS
 *   v1  flat single sheet: { version, order, tierStarts, targets, avoids,
 *       notes, updatedAt }
 *   v2  { version, activeId, sheets: CheatSheet[] }
 *
 * Both shapes parse. v1 fields are kept OPTIONAL here rather than deleted so
 * an old document still validates; `readSheets()` in lib/cheatSheets.ts is the
 * single place that normalises either shape into v2 in memory.
 *
 * `order` is the full custom rank order as player ids (string form, same ids
 * as draftData picks). Players NOT in `order` sort after it by ADP — so a
 * sheet that only reorders the top 50 still ranks the whole pool sensibly.
 */
export const RankingsBlobSchema = z
  .object({
    /** Schema version for forward migrations. */
    version: z.number().int().default(1),

    // ---- v2 ----
    /** Id of the sheet currently being edited. */
    activeId: z.string().optional(),
    /** Every sheet the user owns, in display order. */
    sheets: z.array(CheatSheetSchema).optional(),

    // ---- v1 (legacy, read-only) ----
    order: z.array(z.string()).optional(),
    tierStarts: z.array(z.string()).optional(),
    targets: z.array(z.string()).optional(),
    avoids: z.array(z.string()).optional(),
    notes: z.record(z.string()).optional(),
    /** ISO 8601 client-side timestamp of the last edit. */
    updatedAt: z.string().optional(),
  })
  .passthrough();
export type RankingsBlob = z.infer<typeof RankingsBlobSchema>;

/** GET/PUT /v2/me/rankings response. `rankings: null` = never saved. */
export const RankingsResponseSchema = z.object({
  rankings: RankingsBlobSchema.nullable(),
  updated_at: z.string().nullable().optional(),
});
export type RankingsResponse = z.infer<typeof RankingsResponseSchema>;

// ---------- Generic envelopes --------------------------------------------

/**
 * Used by every endpoint while we stub things out — lets the SPA distinguish
 * "not yet implemented on Laravel side" from real data without breaking types.
 */
export const TodoResponseSchema = z.object({ todo: z.literal(true) });
export type TodoResponse = z.infer<typeof TodoResponseSchema>;

export const ApiErrorSchema = z.object({
  message: z.string(),
  code: z.string().optional(),
  errors: z.record(z.array(z.string())).optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
